import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { FormSelectField } from '@/components/Form/FormField'
import { Checkbox } from '@/components/ui/checkbox'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
} from '@progress/kendo-react-grid'
import { RoleSecuredProperties } from 'api/Clients/Types/RoleSecuredProperties'
import { SecuredPropertiesLabels } from 'api/Clients/Types/SecuredPropertiesLabels'
import { RoleDto } from 'api/Identity/Types/Role'
import { InputFilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import stringComparator from 'utils/stringComparator'
import { uniqueArrayOfObjects } from 'utils/unique/unique'

export type Entity = 'client' | 'trading_account' | 'introducing_broker'

interface Props {
  role: RoleDto
  tradingAccountLabels: SecuredPropertiesLabels[]
  partnershipsAccountLabels: SecuredPropertiesLabels[]
  clientLabels: SecuredPropertiesLabels[]

  onSubmit(values: AddRolePermissionFormValues): Promise<void>
}

interface DataGridFormat {
  name: string
  label: string
  editable: boolean
  readable: boolean
}

export interface AddRolePermissionFormValues {
  role: string
  aggregateType: Entity
  securedProperties: { name: string; editable: boolean; readable: boolean }[]
}

export interface EditRolePermissionFormHandle {
  submit(): Promise<void>
}

function dataToGrid(roleSecuredProperties: RoleSecuredProperties[]): DataGridFormat[] {
  return roleSecuredProperties.map(({ name, editable, readable, label }) => ({
    label,
    editable,
    readable,
    name,
  }))
}

export const EditRolePermissionForm = forwardRef<EditRolePermissionFormHandle, Props>(
  ({ role, clientLabels, tradingAccountLabels, partnershipsAccountLabels, onSubmit }, ref) => {
    const clientsApiClient = useApiClient(ClientsApiClient)
    const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
    const partnershipsApiClient = useApiClient(PartnershipsApiClient)
    const setToast = useContext(ToastContext)

    const [selectedEntity, setSelectedEntity] = useState<Entity>('client')
    const [selectedProperty, setSelectedProperty] = useState<string | number | undefined>()
    const [securedProperties, setSecuredProperties] = useState<DataGridFormat[]>([])
    const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })

    const securedPropertiesMap = useMemo(
      () => ({
        client: {
          labels: clientLabels,
          getSecuredProperties: clientsApiClient.getRoleClientsSecuredProperties,
          deleteSecuredProperty: clientsApiClient.deleteRoleClientsSecuredProperties,
        },
        trading_account: {
          labels: tradingAccountLabels,
          getSecuredProperties:
            tradingAccountsApiClient.getRoleTradingAccountsSecuredPropertiesLabels,
          deleteSecuredProperty:
            tradingAccountsApiClient.deleteRoleTradingAccountsSecuredProperties,
        },
        introducing_broker: {
          labels: partnershipsAccountLabels,
          getSecuredProperties: partnershipsApiClient.getRolePartnershipsSecuredPropertiesLabels,
          deleteSecuredProperty: partnershipsApiClient.deleteRolePartnershipsSecuredProperties,
        },
      }),
      [
        clientLabels,
        tradingAccountLabels,
        partnershipsAccountLabels,
        clientsApiClient,
        tradingAccountsApiClient,
        partnershipsApiClient,
      ]
    )

    const callback = useCallback(
      (query?: PageQuery) => {
        return securedPropertiesMap[selectedEntity].getSecuredProperties({
          ...query,
          roles: role.name,
        })
      },
      [role.name, securedPropertiesMap, selectedEntity]
    )

    const { data: result, isLoading } = useFetchOne(callback)

    useEffect(() => {
      if (result) setSecuredProperties(dataToGrid(result))
    }, [result])

    useEffect(() => {
      setSelectedProperty(undefined)
      setFilter({ logic: 'and', filters: [] })
    }, [selectedEntity])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          await onSubmit({
            role: role.name,
            aggregateType: selectedEntity,
            securedProperties: securedProperties.map(({ readable, editable, name }) => ({
              name,
              editable,
              readable,
            })),
          })
        },
      }),
      [onSubmit, role.name, securedProperties, selectedEntity]
    )

    const handleFilterChange = async (event: GridFilterChangeEvent) => {
      setFilter(event.filter)
    }

    const updateSecuredProperty = (
      name: string,
      propertyName: 'editable' | 'readable',
      value: boolean
    ) => {
      setSecuredProperties(
        securedProperties.map((property) =>
          property.name === name ? { ...property, [propertyName]: value } : property
        )
      )
    }

    const availableProperties = securedPropertiesMap[selectedEntity].labels.filter(
      (label) => !securedProperties.some((property) => property.name === label.id)
    )

    const gridData = filterBy(securedProperties || [], filter).sort((a, b) =>
      stringComparator(a.name, b.name)
    )

    return (
      <Loading isLoading={isLoading}>
        <div className='flex h-full min-h-0 min-w-0 flex-col gap-6 overflow-hidden'>
          <div className='grid shrink-0 gap-4 md:grid-cols-2'>
            <FormSelectField
              name='selectedEntity'
              label='Domain Entity'
              options={[
                { id: 'client', name: 'Clients' },
                { id: 'trading_account', name: 'Trading Accounts' },
                { id: 'introducing_broker', name: 'Introducing Broker' },
              ]}
              value={selectedEntity}
              onChange={(value) => {
                setSelectedEntity(value as Entity)
              }}
            />
            <FormSelectField
              name='selectedProperty'
              label='Add Property'
              options={availableProperties.map((label) => ({
                id: label.id,
                name: label.label,
              }))}
              value={selectedProperty}
              placeholder='Select property'
              allowClear
              showSearch
              onChange={(value) => {
                setSelectedProperty(value)

                const label = securedPropertiesMap[selectedEntity].labels.find(
                  (item) => item.id === value
                )

                if (!label) {
                  return
                }

                setSecuredProperties(
                  uniqueArrayOfObjects(
                    securedProperties.concat({
                      name: label.id,
                      editable: true,
                      readable: true,
                      label: label.label,
                    })
                  )
                )
                setSelectedProperty(undefined)
              }}
            />
          </div>

          <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-3'>
            <div className='shrink-0'>
              <h3 className='font-medium'>Property Overrides</h3>
              <p className='text-sm text-muted-foreground'>
                Adjust readability and editability for the selected domain entity before saving.
              </p>
            </div>

            <div className='min-h-0 min-w-0 flex-1 overflow-hidden'>
              <Grid
                filterable
                resizable
                data={gridData}
                filter={filter}
                total={(securedProperties || []).length}
                onFilterChange={handleFilterChange}
                style={{ zIndex: 0, height: '100%', width: '100%' }}
              >
                <Column
                  title='Property name'
                  field='label'
                  filterCell={InputFilterCell}
                  className='table-columns__content'
                />
                <Column
                  width={160}
                  title='Readable'
                  field='readable'
                  filterCell={() => null}
                  cell={(gridCellProps: GridCellProps) => (
                    <td align='center' style={{ textAlign: 'center' }}>
                      <Checkbox
                        checked={(gridCellProps.dataItem as RoleSecuredProperties).readable}
                        onCheckedChange={(checked) => {
                          updateSecuredProperty(
                            gridCellProps.dataItem.name,
                            'readable',
                            Boolean(checked)
                          )
                        }}
                      />
                    </td>
                  )}
                />
                <Column
                  width={160}
                  title='Editable'
                  field='editable'
                  filterCell={() => null}
                  cell={(gridCellProps: GridCellProps) => (
                    <td align='center' style={{ textAlign: 'center' }}>
                      <Checkbox
                        checked={(gridCellProps.dataItem as DataGridFormat).editable}
                        onCheckedChange={(checked) => {
                          updateSecuredProperty(
                            gridCellProps.dataItem.name,
                            'editable',
                            Boolean(checked)
                          )
                        }}
                      />
                    </td>
                  )}
                />
                <Column
                  width={60}
                  title=''
                  filterCell={() => null}
                  cell={(props: GridCellProps) => (
                    <td>
                      <MoreButton
                        items={[
                          {
                            name: 'Delete',
                            'data-test': 'delete',
                            onClick: async () => {
                              const name =
                                securedProperties.find(
                                  (x) => x.name === (props.dataItem as DataGridFormat).name
                                )?.name ?? ''

                              await securedPropertiesMap[selectedEntity].deleteSecuredProperty({
                                name: name,
                                aggregateType: selectedEntity,
                                role: role.name,
                              })

                              setSecuredProperties(
                                securedProperties.filter(
                                  (x) => x.name !== (props.dataItem as DataGridFormat).name
                                )
                              )
                              setToast(successToast('Property was successfully deleted.'))
                            },
                          },
                        ]}
                        data-test='more-button'
                      />
                    </td>
                  )}
                />
              </Grid>
            </div>
          </div>
        </div>
      </Loading>
    )
  }
)

EditRolePermissionForm.displayName = 'EditRolePermissionForm'
